/**
 * アプリ内の通知音ユーティリティ。
 *
 * 音声ファイルを持たずに Web Audio API でチャイムを合成する（アセット追加なし・
 * 端末ごとの差も少ない）。主に「ITサポートの空き順番が回ってきた（空きが出た）」
 * ことを、アプリを開いている利用者へ音で知らせる用途に使う。
 *
 * - 自動再生ポリシー対策: 最初のユーザー操作（タップ／キー）で AudioContext を resume する。
 * - 多重再生の抑制: 直近 1.5 秒以内の再呼び出しは無視する（ポーリングのブレで連打しない）。
 * - 対応端末では軽く振動させ、スマホでも気づきやすくする。
 */

let audioContext: AudioContext | null = null;
let resumeBound = false;
let lastPlayedAt = 0;

/** AudioContext（未対応ブラウザは null）。 */
function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioContext) {
    try {
      audioContext = new Ctor();
    } catch {
      return null;
    }
  }
  return audioContext;
}

/** 最初のユーザー操作で AudioContext を再開できるよう、一度だけリスナーを張る。 */
function bindResumeOnGesture(): void {
  if (resumeBound || typeof window === 'undefined') return;
  resumeBound = true;
  const resume = () => {
    void audioContext?.resume?.();
  };
  window.addEventListener('pointerdown', resume, { once: true });
  window.addEventListener('keydown', resume, { once: true });
}

/** 通知音（「空きが出た」を伝える2音のピンポン）。 */
export function playNotificationChime(): void {
  const now = Date.now();
  if (now - lastPlayedAt < 1500) return;
  lastPlayedAt = now;

  // スマホでは軽く振動（未対応端末は無視）。
  try {
    navigator.vibrate?.(120);
  } catch {
    // 無視
  }

  const audio = getAudioContext();
  if (!audio) return;
  bindResumeOnGesture();
  try {
    if (audio.state === 'suspended') void audio.resume();

    // 2音（A5 → E6）を短く鳴らす。
    const notes: [number, number][] = [
      [880, 0],
      [1318.51, 0.16],
    ];
    const start = audio.currentTime;
    for (const [freq, offset] of notes) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.22, start + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.2);
      osc.connect(gain).connect(audio.destination);
      osc.start(start + offset);
      osc.stop(start + offset + 0.22);
    }
  } catch {
    // 音が鳴らせない環境では黙って諦める（通知自体はベル／プッシュで担保される）。
  }
}